import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * DateCell — formats an ISO string or Date into a mono cell.
 *
 *   format='short' → 14 May 26
 *   format='long'  → 14 May 2026
 *   format='iso'   → 2026-05-14
 *   format='relative' → "in 12 days" / "3 hours ago"
 */

type DateFormat = 'short' | 'long' | 'iso' | 'relative';

interface DateCellProps {
  value: string | Date | null | undefined;
  format?: DateFormat;
  locale?: string;
  className?: string;
  emptyText?: string;
}

function formatRelative(now: Date, then: Date, locale: string): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diffSeconds = Math.round((then.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  if (abs < 60) return rtf.format(diffSeconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSeconds / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSeconds / (86400 * 30)), 'month');
  return rtf.format(Math.round(diffSeconds / (86400 * 365)), 'year');
}

function formatDate(d: Date, format: DateFormat, locale: string): string {
  if (format === 'iso') return d.toISOString().slice(0, 10);
  if (format === 'relative') return formatRelative(new Date(), d, locale);
  if (format === 'short') {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

function DateCell({
  value,
  format = 'long',
  locale = 'en-ZA',
  className,
  emptyText = '—',
}: DateCellProps) {
  if (value == null) {
    return <span className={cn('text-xs tabular-nums text-muted-foreground', className)}>{emptyText}</span>;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return <span className={cn('text-xs tabular-nums text-muted-foreground', className)}>{emptyText}</span>;
  }
  return (
    <time
      dateTime={date.toISOString()}
      className={cn('text-xs tabular-nums text-muted-foreground', className)}
    >
      {formatDate(date, format, locale)}
    </time>
  );
}

export { DateCell };
export type { DateCellProps, DateFormat };
